import React, { useEffect, useState } from 'react'
import { Button, Card, Col, Container, Form, Row } from 'react-bootstrap'
import { BsArrowLeft, BsCheckLg, BsXLg } from 'react-icons/bs'
import { CalendarCheck } from 'react-bootstrap-icons'
import { LuGraduationCap } from 'react-icons/lu'
import { useLocation, useNavigate } from 'react-router-dom'
import Cookies from 'js-cookie'
import toast from 'react-hot-toast'
import axios from 'axios'
import { useAuth } from './AuthContext'
import { useSidebar } from './SidebarContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import { useInstitute } from './commonComponents/InstituteContext'
import { BASE_URL } from './commonComponents/ApiEndpoints'

export default function CreateHoliday() {
  const { logout, user } = useAuth()
  const { isCollapsed } = useSidebar()
  const { logo } = useInstitute()
  const navigate = useNavigate()
  const location = useLocation()
  const editingHoliday = location.state?.holiday
  const isEditMode = Boolean(editingHoliday)
  const [marginLeft, setMarginLeft] = useState('0px')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )
  const [logoError, setLogoError] = useState(false)
  const [loading, setLoading] = useState(false)
  const [holiday, setHoliday] = useState({
    holiday_type: '',
    date: '',
    from_date: '',
    to_date: '',
    title: '',
    session: '',
    institute_id: '',
  })

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1

        setMarginLeft(
          isCollapsed
            ? `${collapsedWidth + gap}px`
            : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }

      setIsMobile(window.innerWidth <= 768)
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  useEffect(() => {
    setHoliday((prev) => ({
      ...prev,
      id: editingHoliday?.id || prev.id,
      holiday_type: editingHoliday?.holiday_type || prev.holiday_type,
      date: editingHoliday?.date || prev.date,
      title: editingHoliday?.title || prev.title,
      session: Cookies.get('session_name') || Cookies.get('session') || '',
      institute_id: Cookies.get('institute_id') || '',
    }))
  }, [editingHoliday])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setHoliday((prev) => ({ ...prev, [name]: value }))
  }

  const getAuthToken = () => {
    if (user?.token) return user.token

    try {
      const userCookie = Cookies.get('user')
      return userCookie ? JSON.parse(userCookie)?.token : ''
    } catch {
      return ''
    }
  }

  const handleSave = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      const instituteId = Number(holiday.institute_id || Cookies.get('institute_id'))
      const token = getAuthToken()

      if (!Number.isFinite(instituteId) || instituteId <= 0) {
        toast.error('Institute ID not found. Please select institute again.')
        return
      }

      if (!token) {
        toast.error('User session not found. Please login again.')
        return
      }

      if (isEditMode) {
        const payload = {
          id: holiday.id,
          session: holiday.session,
          holiday_type: holiday.holiday_type,
          date: holiday.date,
          title: holiday.title,
        }

        const response = await axios.put(
          `${BASE_URL}/admin/holiday/update`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Institute-Id': instituteId,
              'Content-Type': 'application/json',
            },
          },
        )

        toast.success(response.data?.message || 'Holiday updated successfully')
        navigate('/ManageHolidays', {
          state: { holidayTypeFilter: payload.holiday_type },
        })
        return
      }

      if (!holiday.from_date || !holiday.to_date) {
        toast.error('Please select both From Date and To Date.')
        return
      }

      if (holiday.to_date < holiday.from_date) {
        toast.error('To Date cannot be earlier than From Date.')
        return
      }

      if (!holiday.session) {
        toast.error('Session not found. Please select institute again.')
        return
      }

      const payload = {
        session: holiday.session,
        holiday_type: holiday.holiday_type,
        from_date: holiday.from_date,
        to_date: holiday.to_date,
        title: holiday.title,
      }

      const response = await axios.post(
        `${BASE_URL}/admin/holiday/create`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Institute-Id': instituteId,
            'Content-Type': 'application/json',
          },
        },
      )

      toast.success(response.data?.message || 'Holiday created successfully')
      navigate('/ManageHolidays')
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save holiday')
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <div
        style={{
          marginLeft,
          transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'margin-left',
          WebkitTransition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          zIndex: 1,
          marginTop: '0px',
          marginRight: '0px',
          marginBottom: '0px',
          paddingLeft: isCollapsed ? '12px' : '12px',
          paddingRight: isCollapsed ? '12px' : '12px',
        }}
      >
        <div
          className="container-fluid p-0"
          style={{
            marginTop: typeof window !== 'undefined' && window.innerWidth < 992 ? '55px' : '-12px',
            paddingLeft: '0px',
            paddingRight: '0px',
          }}
        >
          <div style={{ marginLeft: isMobile ? '-13px' : '-40px' }}>
            <div
              className="d-flex justify-content-between align-items-center"
              style={{
                background: '#F3F4F6',
                paddingLeft: '28px',
                paddingRight: '28px',
                paddingTop: '12px',
                paddingBottom: '12px',
                overflow: 'visible',
                borderBottom: '1px solid #D1D3D4',
                width: '100%',
              }}
            >
              <div className="d-flex align-items-center gap-3">
                {/* <button
                  type="button"
                  onClick={() => navigate('/ManageHolidays')}
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    border: '1px solid #D1D3D4',
                    backgroundColor: '#ffffff',
                    color: '#223843',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    flexShrink: 0,
                  }}
                  title="Back"
                >
                  <BsArrowLeft size={18} />
                </button> */}
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    background: '#223843',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {!logoError && logo ? (
                    <img
                      src={logo}
                      alt="School Logo"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={() => setLogoError(true)}
                    />
                  ) : (
                    <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                  )}
                </div>
                <h3
                  className="m-0 fw-bold"
                  style={{
                    letterSpacing: '-0.02em',
                    overflow: 'visible',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: 'clamp(18px, 2.5vw, 22px)',
                    color: '#111827',
                    lineHeight: '1.2',
                    fontFamily: 'Inter, system-ui, sans-serif',
                  }}
                >
                  {isEditMode ? 'Edit Holiday' : 'Create Holiday'}
                </h3>
              </div>

              {!isMobile && <ProfileDropdown logout={logout} />}
            </div>
          </div>
        </div>

        <Container
          fluid
          className="mt-4"
          style={{
            paddingLeft: isMobile ? '12px' : isCollapsed ? '32px' : '40px',
            paddingRight: isMobile ? '12px' : '32px',
            paddingTop: '32px',
            paddingBottom: '32px',
            maxWidth: '100%',
          }}
        >
          <Form onSubmit={handleSave}>
            <Row className="g-4">
              <Col xs={12} className="d-flex">
                <Card
                  className="h-100 w-100"
                  style={{
                    border: '1px solid #D1D3D4',
                    borderRadius: '12px',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      background: 'linear-gradient(135deg, #223843 0%, #223843 100%)',
                      padding: '16px 24px',
                      borderBottom: '1px solid #D1D3D4',
                    }}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <div
                        style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '10px',
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backdropFilter: 'blur(10px)',
                        }}
                      >
                        <CalendarCheck size={18} style={{ color: '#ffffff' }} />
                      </div>
                      <h5
                        style={{
                          margin: 0,
                          fontWeight: '700',
                          fontSize: '16px',
                          color: '#ffffff',
                          fontFamily: 'Inter, system-ui, sans-serif',
                        }}
                      >
                        Holiday Details
                      </h5>
                    </div>
                  </div>
                  <Card.Body style={{ padding: '24px' }} className="text-start">
                    <Form.Group className="mb-3">
                      <Form.Label>Holiday Type *</Form.Label>
                      <Form.Select
                        name="holiday_type"
                        value={holiday.holiday_type}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select Holiday Type</option>
                        <option value="STAFF">STAFF</option>
                        <option value="STUDENT">STUDENT</option>
                        <option value="ALL">ALL</option>
                      </Form.Select>
                    </Form.Group>

                    {isEditMode ? (
                      <Form.Group className="mb-3">
                        <Form.Label>Date *</Form.Label>
                        <Form.Control
                          type="date"
                          name="date"
                          value={holiday.date}
                          onChange={handleInputChange}
                          required
                        />
                      </Form.Group>
                    ) : (
                      <Row className="g-3 mb-3">
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>From Date *</Form.Label>
                            <Form.Control
                              type="date"
                              name="from_date"
                              value={holiday.from_date}
                              onChange={handleInputChange}
                              required
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>To Date *</Form.Label>
                            <Form.Control
                              type="date"
                              name="to_date"
                              value={holiday.to_date}
                              min={holiday.from_date || undefined}
                              onChange={handleInputChange}
                              required
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    )}

                    <Form.Group className="mb-0">
                      <Form.Label>Title *</Form.Label>
                      <Form.Control
                        as="input"
                        type="text"
                        name="title"
                        value={holiday.title}
                        onChange={handleInputChange}
                        placeholder="Enter holiday title"
                        required
                      />
                    </Form.Group>
                  </Card.Body>
                </Card>
              </Col>

            </Row>

            <div className="d-flex justify-content-end gap-3 mt-4 pt-3" style={{ borderTop: '1px solid #E5E7EB' }}>
              <Button
                type="button"
                onClick={() => navigate('/ManageHolidays')}
                disabled={loading}
                style={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #D1D3D4',
                  color: '#6b7280',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  fontWeight: '600',
                  fontSize: '15px',
                  fontFamily: 'Inter, system-ui, sans-serif',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  minWidth: '140px',
                }}
              >
                <BsXLg size={16} />
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                style={{
                  backgroundColor: loading ? '#9ca3af' : '#10B981',
                  border: 'none',
                  color: '#ffffff',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  fontWeight: '600',
                  fontSize: '15px',
                  fontFamily: 'Inter, system-ui, sans-serif',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  minWidth: '160px',
                  boxShadow: loading ? 'none' : '0 2px 4px rgba(16, 185, 129, 0.2)',
                }}
              >
                <BsCheckLg size={16} />
                {loading ? 'Saving...' : isEditMode ? 'Update Holiday' : 'Save Holiday'}
              </Button>
            </div>
          </Form>
        </Container>
      </div>
    </>
  )
}
